import { FC, useCallback, useEffect, useRef } from "react";
import { Stack, Theme, Box } from "@mui/material";
import { ImperativePanelHandle, Panel } from "react-resizable-panels";
import { useOutletContext } from "react-router-dom";
import { InfrastructureArchitectureSelectPanelHeader } from "./InfrastructureArchitectureSelectPanelHeader";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { infrastructureArchitectureSelectorDrawerExpandedSelector } from "@/store/infrastructure-architecture/selectors/infrastructureArchitectureSelectorDrawerExpandedSelector";
import { InfrastructureArchitectureSelectPanelBody } from "@/components/pages/infrastructure-architecture/components/select-panel/body/InfrastructureArchitectureSelectPanelBody";
import { useLayoutConfigHook } from "@/components/layout/utils/hooks/useLayoutConfigHook";
import { OutletContextType } from "@/components/layout/MainLayout";
import { toggleSelectDrawerExpanded } from "@/store/infrastructure-architecture/infrastructureArchitectureSlice";

export const InfrastructureArchitectureSelectPanel: FC = () => {
  const sideBarRef = useRef<ImperativePanelHandle>(null);

  const dispatch = useAppDispatch();
  const selectDrawerExpanded = useAppSelector(
    infrastructureArchitectureSelectorDrawerExpandedSelector,
  );

  const { layoutSizes } = useOutletContext<OutletContextType>();
  const { min } = useLayoutConfigHook(52, minWidth, 500, layoutSizes?.[1]);

  const collapseHandler = useCallback(() => {
    if (!selectDrawerExpanded) {
      return;
    }

    dispatch(toggleSelectDrawerExpanded());
  }, [dispatch, selectDrawerExpanded]);

  const expandHandler = useCallback(() => {
    if (selectDrawerExpanded) {
      return;
    }

    dispatch(toggleSelectDrawerExpanded());
  }, [dispatch, selectDrawerExpanded]);

  const handleCollapseToggle = useCallback(() => {
    if (sideBarRef.current?.isExpanded()) {
      sideBarRef.current?.collapse();
    } else {
      sideBarRef.current?.expand();
    }
  }, []);

  useEffect(() => {
    const expandedFromRef = sideBarRef.current?.isExpanded();

    if (selectDrawerExpanded !== expandedFromRef) {
      handleCollapseToggle();
    }
  }, [selectDrawerExpanded, handleCollapseToggle]);

  return (
    <Panel
      ref={sideBarRef}
      id={panelId}
      order={1}
      collapsible
      minSize={min}
      maxSize={maxSize}
      defaultSize={defaultsSize}
      onCollapse={collapseHandler}
      onExpand={expandHandler}
      style={{ overflowY: "auto" }}
    >
      {selectDrawerExpanded ? (
        <Stack height="100%" width="100%" bgcolor="white">
          <InfrastructureArchitectureSelectPanelHeader />

          <InfrastructureArchitectureSelectPanelBody />
        </Stack>
      ) : (
        <Box
          position="fixed"
          top={25}
          left={75}
          width={360}
          sx={selectDrawerStyles}
        >
          <InfrastructureArchitectureSelectPanelHeader enableLoading />
        </Box>
      )}
    </Panel>
  );
};

const panelId = "infrastructure-architecture-select-panel";

const selectDrawerStyles = {
  bgcolor: "white",
  borderRadius: 2,
  boxShadow: (theme: Theme) => theme.shadows[3],
  zIndex: (theme: Theme) => theme.zIndex.drawer,
};

const maxSize = 40;
const defaultsSize = 20;
const minWidth = 320;
